import React, {useEffect, useState} from 'react';
import {Input} from 'antd';
import EditableCard from 'components/EditableCard';
import { useTranslation } from 'react-i18next';

export interface PickupZoneFormData {
  zipcodes: string[];
}

interface PickupZoneFormProps {
  data: PickupZoneFormData;
  isEditing?: boolean;
  onEdit?: () => void;
  onSave?: (data: PickupZoneFormData) => void;
  onCancel?: () => void;
  title?: string;
  loading?: boolean;
  bordered?: boolean;
}

const PickupZoneForm : React.FC<PickupZoneFormProps> = ({
  data,
  isEditing = false,
  onEdit,
  onSave,
  onCancel,
  title,
  loading = false,
  bordered = false,
}) => {

  const [internalZipcodes, setInternalZipcodes] = useState<string>("");
  const { t } = useTranslation();
  useEffect(() => {
    setInternalZipcodes(data.zipcodes.join(', '));
  }, [data.zipcodes]);

  // 监听编辑状态变化，取消编辑时重置为原始数据
  useEffect(() => {
    if (!isEditing) {
      setInternalZipcodes(data.zipcodes.join(', '));
    }
  }, [isEditing, data.zipcodes]);

  // 解析和验证 zipcode 的函数
  const parseAndValidateZipcodes = (input: string) => {
    // 支持多种分隔符：逗号、空格、换行、分号、制表符
    const rawZipcodes = input
      .split(/[,\s\n\r\t;]+/)
      .map(code => code.trim())
      .filter(code => code.length > 0);

    // 验证每个 zipcode
    const validZipcodes: string[] = [];
    const invalidZipcodes: string[] = [];
    const duplicates: string[] = [];

    rawZipcodes.forEach(code => {
      // zipcode 格式验证：只允许数字
      const isValidFormat = /^\d+$/.test(code);

      if (!isValidFormat) {
        invalidZipcodes.push(code);
      } else if (validZipcodes.includes(code)) {
        if (!duplicates.includes(code)) {
          duplicates.push(code);
        }
      } else {
        validZipcodes.push(code);
      }
    });

    return {
      validZipcodes,
      invalidZipcodes,
      duplicates,
      totalCount: rawZipcodes.length
    };
  };

  const handleSave = () => {
    const { validZipcodes, invalidZipcodes, duplicates } = parseAndValidateZipcodes(internalZipcodes);

    // 如果有无效数据或重复数据，阻止保存
    if (invalidZipcodes.length > 0 || duplicates.length > 0) {
      return; // 不执行保存操作
    }

    onSave?.({ zipcodes: validZipcodes });
  };

  // 计算统计信息
  const {
    validZipcodes,
    invalidZipcodes,
    duplicates,
    totalCount
  } = parseAndValidateZipcodes(internalZipcodes);

  return (
    <EditableCard
      title={title || t('central.sites.details.pickupZone')}
      isEditing={isEditing}
      onEdit={onEdit}
      onSave={handleSave}
      onCancel={onCancel}
      loading={loading}
      bordered={bordered}
    >
      <Input.TextArea
        value={internalZipcodes}
        rows={4}
        readOnly={!isEditing}
        onChange={(e) => setInternalZipcodes(e.target.value)}
        placeholder={isEditing ? "Enter zipcodes separated by commas, spaces, or new lines (e.g., 12345, 67890, 54321)" : ""}
      />
      <div className="mt-2 space-y-1">
        <div className="text-gray-500">
          {t('central.sites.details.totalEntries')}: {totalCount} | {t('central.sites.details.valid')}: {validZipcodes.length}
        </div>

        {duplicates.length > 0 && (
          <div className="text-orange-500 text-sm">
            {duplicates.length} duplicate zipcode{duplicates.length > 1 ? 's' : ''}: {duplicates.join(', ')}
          </div>
        )}

        {invalidZipcodes.length > 0 && (
          <div className="text-red-500 text-sm">
            {invalidZipcodes.length} invalid zipcode{invalidZipcodes.length > 1 ? 's' : ''}: {invalidZipcodes.slice(0, 3).join(', ')}{invalidZipcodes.length > 3 ? '...' : ''}
            <div className="text-xs mt-1">
              Zipcodes must be numbers only
            </div>
          </div>
        )}

        {(invalidZipcodes.length > 0 || duplicates.length > 0) && (
          <div className="text-red-500 text-sm font-medium">
             Cannot save: Please fix the issues above
          </div>
        )}


      </div>
    </EditableCard>
  );
};

export default PickupZoneForm;